import { Link } from 'react-router-dom';
import { Store, TrendingUp, Package, Eye } from 'lucide-react';
import { formatPrice, formatPricePerUnit } from '../utils/formatters';
import { getProductUrl } from '../utils/url-helpers';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';
import { useState, memo, lazy, Suspense } from 'react';
import StockBadge from './StockBadge';
import SpecsPreview from './SpecsPreview';
import CompareButton from './CompareButton';

const QuickViewModal = lazy(() => import('./QuickViewModal'));

const ProductCard = memo(function ProductCard({ product }) {
  const { language, languageConfig } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const {
    id,
    brand,
    name,
    category,
    minPrice,
    maxPrice,
    vendorCount,
    images = [],
    currency = 'CZK',
    tags = [],
    specifications = {},
    offers = []
  } = product;

  const priceRange = minPrice === maxPrice
    ? formatPrice(minPrice, currency, languageConfig.locale)
    : `${formatPrice(minPrice, currency, languageConfig.locale)} - ${formatPrice(maxPrice, currency, languageConfig.locale)}`;

  // Per-unit price (e.g. "39,80 Kč / 100 g") — only when the package has a
  // comparable mass/volume size, to aid cross-product comparison.
  const pricePerUnitLabel = formatPricePerUnit(product.pricePerUnit, currency, languageConfig.locale);

  const productImage = images[0] ? (typeof images[0] === 'string' ? images[0] : images[0].url) : null;

  return (
    <>
      <Link
        to={getProductUrl(product)}
        className="card group overflow-hidden block animate-fade-in relative"
      >
        {/* Compare Button */}
        <CompareButton productId={id} />

        {/* Quick View Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsQuickViewOpen(true);
          }}
          className="absolute top-3 right-3 z-10 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all duration-200 hover:bg-white min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label={t('quickView') || 'Quick View'}
        >
          <Eye className="w-4 h-4 text-gray-700" aria-hidden="true" />
        </button>

        {/* Stock Status Badge */}
        <div className="absolute top-3 left-3 z-10">
          <StockBadge offers={offers} />
        </div>

        {/* Product Image */}
        <div className="aspect-square bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center overflow-hidden">
          {productImage ? (
            <img
              src={productImage}
              alt={name}
              width="400"
              height="400"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <Package className="w-16 h-16 text-primary-400 group-hover:scale-110 transition-transform duration-300" aria-hidden="true" />
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="badge bg-gray-100 text-gray-700 text-xs">
              {category || 'Uncategorized'}
            </span>
          </div>

          {brand && (
            <p className="text-xs font-medium text-primary-600 uppercase tracking-wide">
              {brand}
            </p>
          )}

          <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-primary-600 transition-colors text-sm">
            {name}
          </h3>

          <SpecsPreview specifications={specifications} />

          <div className="flex items-baseline space-x-2">
            <span className="text-lg font-bold text-gray-900">
              {priceRange}
            </span>
          </div>

          {pricePerUnitLabel && (
            <p className="text-xs text-gray-500" title={t('pricePerUnit') || 'Price per unit'}>
              {pricePerUnitLabel}
            </p>
          )}

          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 2).map((tag, index) => (
                <span
                  key={index}
                  className="text-xs px-2 py-1 bg-primary-50 text-primary-700 rounded-full"
                >
                  #{tag.name}
                </span>
              ))}
              {tags.length > 2 && (
                <span className="text-xs px-2 py-1 text-gray-600">
                  +{tags.length - 2}
                </span>
              )}
            </div>
          )}

          {vendorCount > 0 && (
            <div
              className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-lg font-medium text-xs ${
                vendorCount === 1
                  ? 'bg-gray-100 text-gray-700'
                  : vendorCount <= 3
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-green-100 text-green-700'
              }`}
            >
              <Store className="w-4 h-4" />
              <span>
                {vendorCount} {vendorCount === 1 ? t('vendor') : t('vendors')}
              </span>
            </div>
          )}

          <div className="pt-2">
            <span className="text-sm font-medium text-primary-600 group-hover:text-primary-700 flex items-center space-x-1">
              <span>{t('comparePrices')}</span>
              <TrendingUp className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>

      {isQuickViewOpen && (
        <Suspense fallback={null}>
          <QuickViewModal
            productId={id}
            isOpen={isQuickViewOpen}
            onClose={() => setIsQuickViewOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
});

export default ProductCard;
